import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/currency';

const AccountSummary = ({ account, reconciliationStatus, onEdit, onDelete }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getAccountIcon = (type) => {
    const iconMap = {
      'Chequing': 'Wallet',
      'Savings': 'PiggyBank',
      'Investment': 'TrendingUp',
      'Credit Card': 'CreditCard',
      'Line of Credit': 'FileText',
      'Loan': 'FileText',
      'Cash': 'Banknote'
    };
    return iconMap?.[type] || 'Wallet';
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-20 md:p-24">
      <div className="flex items-start justify-between mb-20">
        <div className="flex-1">
          <div className="flex items-center gap-12 mb-8">
            <Icon name={getAccountIcon(account?.type)} size={24} color="var(--color-primary)" />
            <h2 className="text-2xl font-heading font-semibold text-card-foreground">
              {account?.name}
            </h2>
          </div>
          <div className="flex items-center gap-12">
            <span className="inline-flex items-center px-12 py-4 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {account?.type}
            </span>
            <span className="inline-flex items-center px-12 py-4 rounded-full text-xs font-medium bg-muted text-muted-foreground">
              {account?.group}
            </span>
          </div>
        </div>
        {onEdit && onDelete && (
          <div className="flex gap-8">
            <Button
              variant="outline"
              size="icon"
              iconName="Settings"
              onClick={onEdit}
            />
            <Button
              variant="outline"
              size="icon"
              iconName="Trash2"
              onClick={onDelete}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
        <div className="bg-muted rounded-lg p-16">
          <div className="flex items-center gap-8 mb-8">
            <Icon name="DollarSign" size={16} className="text-muted-foreground" />
            <p className="text-sm font-caption text-muted-foreground">
              {account?.group === 'Liabilities' ? 'Amount Owed' : 'Current Balance'}
            </p>
          </div>
          <p className={`text-2xl font-heading font-bold ${
            account?.group === 'Liabilities' 
              ? (account?.balance > 0 ? 'text-red-600' : 'text-green-600')
              : (account?.balance >= 0 ? 'text-green-600' : 'text-red-600')
          }`}>
            {account?.group === 'Liabilities' ? formatCurrency(Math.abs(account?.balance)) : formatCurrency(account?.balance)}
          </p>
          {account?.group === 'Liabilities' && account?.balance > 0 && (
            <p className="text-xs text-red-600 mt-4">
              Outstanding balance to pay
            </p>
          )}
          {account?.group === 'Liabilities' && account?.balance <= 0 && (
            <p className="text-xs text-green-600 mt-4">
              No outstanding balance
            </p>
          )}
        </div>

        <div className="bg-muted rounded-lg p-16">
          <div className="flex items-center gap-8 mb-8">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <p className="text-sm font-caption text-muted-foreground">Last Reconciled</p>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {formatDate(reconciliationStatus?.lastReconciled)}
          </p>
        </div>

        <div className="bg-muted rounded-lg p-16">
          <div className="flex items-center gap-8 mb-8">
            <Icon name="CheckCircle" size={16} className="text-muted-foreground" />
            <p className="text-sm font-caption text-muted-foreground">Uncleared Transactions</p>
          </div>
          <p className="text-lg font-heading font-semibold text-foreground">
            {reconciliationStatus?.unclearedCount || 0}
          </p>
        </div>
      </div>

      {/* Credit Limit Section for Line of Credit and Credit Card */}
      {(account?.type === 'Line of Credit' || account?.type === 'Credit Card') && account?.creditLimit && (
        <div className="mt-16">
          <div className="bg-muted rounded-lg p-16">
            <div className="flex items-center gap-8 mb-8">
              <Icon name="CreditCard" size={16} className="text-muted-foreground" />
              <p className="text-sm font-caption text-muted-foreground">Credit Limit</p>
            </div>
            <p className="text-lg font-heading font-semibold text-foreground">
              {formatCurrency(account?.creditLimit)}
            </p>
            {account?.balance > 0 && (
              <p className="text-xs text-muted-foreground mt-4">
                Available: {formatCurrency(account?.creditLimit - Math.abs(account?.balance))}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSummary;